import { z } from 'zod';
import { uuidSchema } from './common.js';

/**
 * Wire DTOs for the SIL-437 (W3) carbon-targets + org sustainability-config
 * CRUD surface (`/api/client/sustainability/*`, spec §4 of
 * resources/plans/sustainability-forecasting-spec.md), backed by
 * `carbon_target` (718) and `customer_org_sustainability_config` (719).
 *
 * Bare-JSON success responses (no `{data:...}` envelope), matching
 * financials.ts / financials-policy.ts. Errors use `{error:{code,message}}`.
 *
 * SCOPE NOTE: `carbon_target.scope_type` is `org|team` ONLY in v1 —
 * `data_center` was dropped from the spec's original org|data_center|team
 * (see migration 718's header) because data_center is seller_org-owned, not
 * customer_org-owned. Every DTO/route below reflects org|team only.
 */

// ---------------------------------------------------------------------------
// 1. carbon_target
// ---------------------------------------------------------------------------

export const carbonTargetScopeTypeSchema = z.enum(['org', 'team']);
export type CarbonTargetScopeType = z.infer<typeof carbonTargetScopeTypeSchema>;

/**
 * SIL-474: the two shapes a `carbon_target` row can take (migration 722's
 * `target_type` column). `absolute_budget` is the original 718 shape (a
 * flat annual tCO2e/yr budget); `reduction_percent` is org-scope-only (DB
 * CHECK `carbon_target_reduction_percent_org_scope_only`) and expresses a
 * percent reduction vs a customer-entered base-year emissions figure (e.g.
 * "40% below FY2019 by FY2030"). An org has AT MOST ONE headline target,
 * EITHER type, never both — `carbon_target_org_headline_idx` (718) is
 * unchanged and enforces this for free (see migration 722 header).
 */
export const carbonTargetTypeSchema = z.enum(['absolute_budget', 'reduction_percent']);
export type CarbonTargetType = z.infer<typeof carbonTargetTypeSchema>;

/**
 * Auto-stamped, DERIVED-not-stored basis constant (ticket: "location-based
 * basis is derived, not stored input" — spec §4). The carbon kernel's
 * use-phase lens is location-based only (SIL-344), for EITHER target type,
 * so this stays a compile-time literal stamped onto every row at read time,
 * never a column on `carbon_target`.
 *
 * NOTE: this file previously also stamped a hardcoded `CARBON_TARGET_TYPE =
 * 'absolute'` literal onto every row's `type` field — that was true when v1
 * shipped exactly one target shape (718). SIL-474 (722) adds a real,
 * per-row `target_type` column, so `type` is no longer derivable as a
 * constant; `carbonTargetRowSchema` below reads the real
 * `targetType` off the row instead (see `carbonTargetTypeSchema`).
 */
export const CARBON_TARGET_BASIS = 'location_based' as const;

/** A stored `carbon_target` row, plus the one derived-not-stored field
 * (`basis`). */
export const carbonTargetRowSchema = z.object({
  id: uuidSchema,
  customerOrgId: uuidSchema,
  scopeType: carbonTargetScopeTypeSchema,
  /** null for the org headline row; set for a per-team row. */
  teamId: uuidSchema.nullable(),
  /** SIL-474 (722): absolute_budget or reduction_percent — see
   * `carbonTargetTypeSchema`. Every pre-722 row reads as `absolute_budget`
   * (the column's DB DEFAULT). */
  targetType: carbonTargetTypeSchema,
  /** Absolute flat annual budget, tCO2e/yr. NON-NULL only for
   * `targetType='absolute_budget'`; NULL for `targetType='reduction_percent'`
   * (DB CHECK `carbon_target_type_fields_coherence`, 722). A reader must
   * branch on `targetType`, never assume this is populated. */
  tco2ePerYr: z.coerce.number().min(0).nullable(),
  /** Optional for `absolute_budget` (718's original optional disclosure base
   * year); REQUIRED for `reduction_percent`, where it is the anchor year
   * the reduction is measured against (must be strictly before
   * `targetYear` — 722's coherence CHECK). Same column for both shapes —
   * see migration 722 header for why this is NOT two separate columns. */
  baseYear: z.number().int().nullable(),
  /** reduction_percent only: customer-entered base-year emissions (tCO2e).
   * NULL for absolute_budget. This is a CUSTOMER-PROVIDED input, not a
   * platform-computed figure — surface it with a "customer-provided" basis
   * tag everywhere it is disclosed (722's column comment). */
  baseYearTco2e: z.coerce.number().min(0).nullable(),
  /** reduction_percent only: the year the reduction is due. Strictly after
   * `baseYear`. NULL for absolute_budget. */
  targetYear: z.number().int().nullable(),
  /** reduction_percent only: percent reduction vs `baseYearTco2e`, e.g.
   * 40.00 for "40% below". (0, 100]. NULL for absolute_budget. */
  reductionPct: z.coerce.number().gt(0).max(100).nullable(),
  recalcPolicyNote: z.string().nullable(),
  /** Derived, never stored — see CARBON_TARGET_BASIS. */
  basis: z.literal(CARBON_TARGET_BASIS),
  createdBy: uuidSchema.nullable(),
  updatedBy: uuidSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
/** The wire row. Distinct from the carbon kernel's `CarbonTargetRow`, which is
 * the narrower pure-input slice (no derived/audit fields) — do not merge them. */
export type CarbonTargetDto = z.infer<typeof carbonTargetRowSchema>;

/**
 * Body for `PUT /sustainability/targets/team/:teamId` (admin-only).
 * TEAM ROWS ARE ABSOLUTE_BUDGET ONLY — `carbon_target_reduction_percent_
 * org_scope_only` (722) rejects a reduction_percent row at any scope other
 * than 'org', so the team write body is unchanged by SIL-474 (no
 * `targetType` field — every team row this endpoint writes is implicitly
 * `absolute_budget`). `scopeType` / `teamId` are NOT part of the body —
 * they're derived from the route, so a caller can never desync the two the
 * way a single polymorphic-body PUT could.
 */
export const carbonTargetWriteSchema = z.object({
  tco2ePerYr: z.coerce.number().min(0),
  /** Optional base year; once set, spec §4 disclosure copy notes the GHG
   * Protocol mandates a recalculation policy (UI-side copy, not enforced
   * here as a hard requirement on recalcPolicyNote — see route header for
   * the left-ambiguous validation note). */
  baseYear: z.number().int().min(1900).max(2200).nullable().optional(),
  recalcPolicyNote: z.string().trim().max(2000).nullable().optional(),
});
export type CarbonTargetWrite = z.infer<typeof carbonTargetWriteSchema>;

// ---------------------------------------------------------------------------
// SIL-474: `PUT /sustainability/targets/org` write body — a discriminated
// union on `targetType`, since the org row is the ONLY scope that may hold a
// reduction_percent target (722's org-scope-only CHECK). Each branch's own
// required/optional fields mirror the DB's `carbon_target_type_fields_
// coherence` CHECK exactly, so a request that would violate the DB
// constraint is already rejected by zod before it reaches the query.
// ---------------------------------------------------------------------------

const carbonTargetOrgAbsoluteWriteSchema = z.object({
  /** Defaulted to 'absolute_budget' when omitted entirely (pre-SIL-474
   * callers never sent this field) — see `carbonTargetOrgWriteSchema`'s
   * preprocess step, which injects this default before the discriminated
   * union dispatches on it. */
  targetType: z.literal('absolute_budget').default('absolute_budget'),
  tco2ePerYr: z.coerce.number().min(0),
  baseYear: z.number().int().min(1900).max(2200).nullable().optional(),
  recalcPolicyNote: z.string().trim().max(2000).nullable().optional(),
});

const carbonTargetOrgReductionPercentWriteSchema = z.object({
  targetType: z.literal('reduction_percent'),
  baseYear: z.number().int().min(1900).max(2200),
  baseYearTco2e: z.coerce.number().min(0),
  targetYear: z.number().int().min(1900).max(2300),
  reductionPct: z.coerce.number().gt(0).max(100),
  recalcPolicyNote: z.string().trim().max(2000).nullable().optional(),
});

const carbonTargetOrgWriteUnionSchema = z.discriminatedUnion('targetType', [
  carbonTargetOrgAbsoluteWriteSchema,
  carbonTargetOrgReductionPercentWriteSchema,
]);

/**
 * `PUT /sustainability/targets/org` body. Accepts a raw object with NO
 * `targetType` key (pre-SIL-474 shape) by injecting the
 * `absolute_budget` default before the discriminated union dispatches —
 * `z.discriminatedUnion` itself requires the discriminant key to be
 * PRESENT in the raw input to select a branch, so a plain per-field
 * `.default()` on the literal alone is not enough when the key is missing
 * entirely (only when it is `undefined`).
 *
 * The `targetYear > baseYear` rule (722's coherence CHECK) is enforced by
 * the `superRefine` below, applied to the whole union rather than to the
 * reduction-percent branch alone — `z.discriminatedUnion` requires every
 * member to be a plain `ZodObject` (a `.refine()`d branch would no longer
 * qualify).
 */
export const carbonTargetOrgWriteSchema = z
  .preprocess((val) => {
    if (val !== null && typeof val === 'object' && !('targetType' in val)) {
      return { targetType: 'absolute_budget', ...val };
    }
    return val;
  }, carbonTargetOrgWriteUnionSchema)
  .superRefine((val, ctx) => {
    if (val.targetType === 'reduction_percent' && val.targetYear <= val.baseYear) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'targetYear must be after baseYear',
        path: ['targetYear'],
      });
    }
  });
export type CarbonTargetOrgWrite = z.infer<typeof carbonTargetOrgWriteSchema>;

/** `GET /sustainability/targets` response — org headline (nullable, empty
 * state) + every team row the caller's RLS scope can see (team clause,
 * migration 718). */
export const carbonTargetsResponseSchema = z.object({
  org: carbonTargetRowSchema.nullable(),
  teams: z.array(carbonTargetRowSchema),
});
export type CarbonTargetsResponse = z.infer<typeof carbonTargetsResponseSchema>;

/**
 * SIL-474 — FY target card progress: what fraction of the REQUIRED
 * reduction (`baseYearTco2e * reductionPct/100`) has been achieved by
 * `currentAnnualTco2e`, relative to `baseYearTco2e`.
 *
 * `1` = target fully met exactly at the target year's current-annual rate;
 * `< 1` = still short of the target; `> 1` = over-achieved (current annual
 * emissions are already below the target-year goal); negative = current
 * annual emissions are ABOVE the base year (net INCREASE, not a reduction
 * at all). Never clamped here — the FY target card clamps for the visual
 * progress-bar width only, so a caller that wants the raw signed ratio
 * (e.g. to show "12% over target") still gets it from this function.
 *
 * Returns `0` when `baseYearTco2e * reductionPct/100` is `<= 0` (a
 * degenerate reduction of zero tCO2e — reductionPct's own CHECK (722)
 * already keeps it in `(0, 100]`, so this only guards `baseYearTco2e <= 0`)
 * rather than dividing by zero / a negative denominator.
 *
 * @pure
 */
export function reductionTargetProgress(
  baseYearTco2e: number,
  currentAnnualTco2e: number,
  reductionPct: number,
): number {
  const requiredReductionTco2e = baseYearTco2e * (reductionPct / 100);
  if (requiredReductionTco2e <= 0) return 0;
  return (baseYearTco2e - currentAnnualTco2e) / requiredReductionTco2e;
}

// ---------------------------------------------------------------------------
// 2. customer_org_sustainability_config
// ---------------------------------------------------------------------------

export const ghgPurchaseCategorySchema = z.enum(['cat1', 'cat2']);
export type GhgPurchaseCategoryDto = z.infer<typeof ghgPurchaseCategorySchema>;

/** Every column's own DB DEFAULT — mirrored here so the API can serve these
 * exact values when an org has never customized its config row (migration
 * 719: "Absence of a row for an org means 'use every column's own
 * DEFAULT'"). Kept in lockstep with 719's CREATE TABLE defaults. */
export const SUSTAINABILITY_CONFIG_DEFAULTS = {
  operated: true,
  purchaseGhgCategory: 'cat2' as GhgPurchaseCategoryDto,
  disposalWeeeFactor: 21.28,
  usedTransportKm: 2000.0,
  usedRefurbPct: 0.025,
};

/** `GET /sustainability/config` response — always fully populated (falls
 * back to SUSTAINABILITY_CONFIG_DEFAULTS when no row exists yet).
 * `updatedBy`/`updatedAt` are null in that empty-state case. */
export const sustainabilityConfigRowSchema = z.object({
  operated: z.boolean(),
  purchaseGhgCategory: ghgPurchaseCategorySchema,
  disposalWeeeFactor: z.coerce.number().min(0),
  usedTransportKm: z.coerce.number().min(0),
  usedRefurbPct: z.coerce.number().min(0).max(1),
  updatedBy: uuidSchema.nullable(),
  updatedAt: z.string().nullable(),
});
export type SustainabilityConfigRow = z.infer<typeof sustainabilityConfigRowSchema>;

/** Body for `PUT /sustainability/config` (admin-only). Partial patch —
 * omitted fields keep their current stored (or default) value. */
export const sustainabilityConfigWriteSchema = z.object({
  operated: z.boolean().optional(),
  purchaseGhgCategory: ghgPurchaseCategorySchema.optional(),
  disposalWeeeFactor: z.coerce.number().min(0).optional(),
  usedTransportKm: z.coerce.number().min(0).optional(),
  usedRefurbPct: z.coerce.number().min(0).max(1).optional(),
});
export type SustainabilityConfigWrite = z.infer<typeof sustainabilityConfigWriteSchema>;

// ---------------------------------------------------------------------------
// 3. grid_intensity_factor (720) — target-location picker options (SIL-441,
//    W4 of the spec §3/§6: the buy + move wizard steps show "country +
//    g/kWh" for the location a hypothetical buy lands in or a cohort moves
//    to).
// ---------------------------------------------------------------------------

/**
 * One selectable target location. `countryIso3` is the Boavizta trigram the
 * carbon kernel keys on (`CarbonBuyCohortInput.targetUsageLocation` /
 * `CarbonMoveCohortInput.targetUsageLocation`), NOT a display-only label —
 * whatever the picker sends here is what `POST /scenarios/:id/compare`
 * resolves a `grid_intensity_factor` row for.
 *
 * `source`/`sourceVersion`/`referenceYear` ride along because the factor's
 * provenance is disclosed in the scenario calculation basis (720's own
 * column comments) — the picker shows the number, the basis drawer shows
 * where it came from, and both must be reading the same row.
 */
export const gridIntensityOptionSchema = z.object({
  countryIso3: z.string().length(3),
  gCo2ePerKwh: z.coerce.number().nonnegative(),
  source: z.string(),
  sourceVersion: z.string(),
  referenceYear: z.number().int(),
  /**
   * True for the synthetic WOR world-average row only. Surfaced as a flag
   * rather than left for the client to string-match 'WOR', because a MOVE
   * may never target it (spec §3 "moves to 'No location' disallowed",
   * enforced server-side by `assertMoveTargetLocationAllowed`) — the picker
   * needs a first-class way to disable exactly that option.
   */
  isNoLocationFloor: z.boolean(),
});
export type GridIntensityOption = z.infer<typeof gridIntensityOptionSchema>;

/** `GET /sustainability/grid-intensity` response — one row per country,
 * newest `source_version` per country (same per-country resolution
 * `resolveGridIntensityFactor` performs at compute time, so the g/kWh shown
 * in the picker is the g/kWh the kernel will use). */
export const gridIntensityOptionsResponseSchema = z.object({
  options: z.array(gridIntensityOptionSchema),
});
export type GridIntensityOptionsResponse = z.infer<typeof gridIntensityOptionsResponseSchema>;

/**
 * The location every target-location picker leads with and pre-selects: the
 * United States, as Boavizta's ISO alpha-3 trigram (the same key
 * `grid_intensity_factor.country_iso3` uses). Most readers of this product
 * are modeling US facilities, so USA-first removes a scroll from the common
 * path — it is an ORDERING and DEFAULTING preference only and never changes
 * what any other country's factor is.
 */
export const USAGE_LOCATION_PICKER_DEFAULT = 'USA';

/**
 * Lifts `USAGE_LOCATION_PICKER_DEFAULT` to the head of a picker's option list
 * and leaves every other option in the order the API returned it (the route
 * already orders by country, and re-sorting the tail here would fight it).
 *
 * Lives in shared, not in a component: the buy row and the move row both
 * render the same list, and two independently sorted copies of one list is
 * exactly how they drift.
 */
export function sortGridIntensityOptionsForPicker<T extends { countryIso3: string }>(
  options: readonly T[],
): T[] {
  return [
    ...options.filter((o) => o.countryIso3 === USAGE_LOCATION_PICKER_DEFAULT),
    ...options.filter((o) => o.countryIso3 !== USAGE_LOCATION_PICKER_DEFAULT),
  ];
}

/**
 * The location a NEWLY created buy/move row should start on, or `null` when
 * there is nothing safe to pre-select.
 *
 * DELIBERATELY NOT "the first option". If `grid_intensity_factor` carries no
 * USA row the answer is `null` — an unselected picker the reader must fill in.
 * Silently defaulting to some arbitrary other country would change the carbon
 * number under a reader who never chose it, which is strictly worse than
 * making them pick. `null` is also the answer while the options are still
 * loading, so the default cannot fire before the data exists.
 */
export function resolveDefaultUsageLocation(
  options: readonly { countryIso3: string }[] | undefined,
): string | null {
  return (options ?? []).some((o) => o.countryIso3 === USAGE_LOCATION_PICKER_DEFAULT)
    ? USAGE_LOCATION_PICKER_DEFAULT
    : null;
}

// ---------------------------------------------------------------------------
// 4. Buy catalog (SIL-441) — the buy-cohort step's SKU picker. Coverage
//    resolves DYNAMICALLY from `v_sku_carbon_footprint_latest` (spec §3
//    "Buy classes v1: server, gpu, cpu, memory — picker coverage resolves
//    dynamically from v_sku_carbon_footprint_latest"), never from a
//    hardcoded class list: a class becomes buyable the moment one of its
//    SKUs gains a published embodied-carbon row.
// ---------------------------------------------------------------------------

/** An asset class the picker CAN offer — at least one of its SKUs resolves
 * a `v_sku_carbon_footprint_latest` row. */
export const buyCatalogClassSchema = z.object({
  assetClassId: z.string(),
  assetClassName: z.string(),
  skuCount: z.number().int().nonnegative(),
});
export type BuyCatalogClass = z.infer<typeof buyCatalogClassSchema>;

/**
 * Why a class is absent from the picker. TWO DISTINCT reasons, never
 * collapsed into one "unavailable" (spec §3: "Storage excluded by transfer
 * policy; switches by no-data; both disclosed with distinct reasons") —
 * "we won't model this" and "we can't model this yet" are different
 * statements to a reader deciding whether to wait for the feature.
 */
export const buyCatalogExclusionReasonSchema = z.enum(['transfer_policy', 'no_data']);
export type BuyCatalogExclusionReason = z.infer<typeof buyCatalogExclusionReasonSchema>;

/** The distinct disclosure sentence per exclusion reason. Not SIL-426
 * locked copy (that file locks no string for picker coverage) — deliberately
 * factual and claim-free: neither sentence asserts anything about emissions,
 * so neither is subject to the §7 legal-consistency conventions. */
export const BUY_CATALOG_EXCLUSION_NOTES: Record<BuyCatalogExclusionReason, string> = {
  transfer_policy:
    'Excluded from purchase modeling by transfer policy — this class cannot be modeled as a ' +
    'hypothetical purchase.',
  no_data:
    'No published embodied-carbon data for any SKU in this class yet, so it cannot be modeled as a ' +
    'hypothetical purchase.',
};

/**
 * Classes excluded by policy regardless of data coverage. `storage` is the
 * spec's named transfer-policy exclusion; a class NOT in this list that is
 * still missing from `classes` is a `no_data` exclusion, which is how the
 * two reasons stay mechanically distinguishable instead of hand-labeled.
 */
export const BUY_CATALOG_POLICY_EXCLUDED_ASSET_CLASS_IDS: readonly string[] = ['storage'];

export const buyCatalogExcludedClassSchema = z.object({
  assetClassId: z.string(),
  assetClassName: z.string(),
  reason: buyCatalogExclusionReasonSchema,
  note: z.string(),
});
export type BuyCatalogExcludedClass = z.infer<typeof buyCatalogExcludedClassSchema>;

/** One offerable SKU. `embodiedKgco2eqPerUnit` is the per-unit figure the
 * compare route re-resolves server-side (`resolveBuySku`) — shown here so
 * the picker can rank and preview, never as the number the forecast is
 * computed from. */
export const buyCatalogSkuSchema = z.object({
  skuId: uuidSchema,
  assetClassId: z.string(),
  assetClassName: z.string(),
  /** Never blank — the route resolves display_name → model_name →
   * verified_sku in SQL. `.min(1)` is the tripwire: a blank label is a data
   * defect that must fail loudly at the boundary, not ship as an empty
   * `<option>` in the buy-cohort picker. */
  displayName: z.string().min(1),
  manufacturerName: z.string().nullable(),
  embodiedKgco2eqPerUnit: z.coerce.number().nonnegative(),
  /**
   * How many of the CALLER'S OWN assets already run this SKU (RLS-scoped,
   * counted on the same `latestPricingSnapshotId` inner join
   * `resolveCohortClassAggregates` uses, so a cohort's asset count and this
   * count agree on which assets exist). Drives the "buy more of this cohort"
   * shortcut: the SKU behind an owned cohort is its class's most-held SKU.
   */
  ownedUnitCount: z.number().int().nonnegative(),
});
export type BuyCatalogSku = z.infer<typeof buyCatalogSkuSchema>;

/** `GET /sustainability/buy-catalog` response. */
export const buyCatalogResponseSchema = z.object({
  classes: z.array(buyCatalogClassSchema),
  excludedClasses: z.array(buyCatalogExcludedClassSchema),
  skus: z.array(buyCatalogSkuSchema),
});
export type BuyCatalogResponse = z.infer<typeof buyCatalogResponseSchema>;
